import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { UserCheck, Plus, Search, Pencil, Wallet, History } from "lucide-react";
import { useStaff, useCreateStaff, useUpdateStaff, CreateStaffInput } from "../api/staff";
import {
  useRecordSalary,
  useSalarySummary,
  useSalaryTransactions,
  useStaffLedgerSummary,
} from "../api/salaries";
import { StaffProfile, Role, SalaryTxnType } from "../types/models";
import { PageHeader } from "../components/ui/PageHeader";
import { StatCard } from "../components/ui/StatCard";
import { Pagination } from "../components/ui/Pagination";
import { ActiveBadge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { Button, SelectField, TextField } from "../components/ui/Form";
import { LoadingState, ErrorState, EmptyState } from "../components/ui/States";
import { toast } from "../components/ui/Toast";
import { getErrorMessage } from "../lib/errors";
import { formatCurrency, formatDate, formatDateTime, humanizeEnum, toNumber } from "../utils/helpers";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const TXN_TYPES: SalaryTxnType[] = ["SALARY", "ADVANCE", "OVERTIME", "BONUS", "DEDUCTION"];

const TXN_BADGE: Record<SalaryTxnType, string> = {
  SALARY: "bg-blue-500/15 text-blue-300",
  ADVANCE: "bg-amber-500/15 text-amber-300",
  OVERTIME: "bg-violet-500/15 text-violet-300",
  BONUS: "bg-emerald-500/15 text-emerald-300",
  DEDUCTION: "bg-red-500/15 text-red-300",
};

const currentMonth = () => new Date().toISOString().slice(0, 7);

const STAFF_LOGIN_ROLES: Role[] = [
  Role.MANAGER,
  Role.STORE_KEEPER,
  Role.PRODUCTION_SUPERVISOR,
];

export const Staff: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, 350);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<StaffProfile | null>(null);
  const [payFor, setPayFor] = useState<StaffProfile | null>(null);
  const [historyFor, setHistoryFor] = useState<StaffProfile | null>(null);

  const month = currentMonth();
  const summary = useSalarySummary(month);
  const { data, isLoading, isError, error, refetch, isFetching } = useStaff({
    page,
    limit: 10,
    search: search || undefined,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Staff"
        subtitle="Employees, payroll and salary disbursement."
        actions={
          <Button icon={<Plus size={18} />} onClick={() => setCreating(true)}>
            Add Staff
          </Button>
        }
      />

      {summary.data && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <StatCard icon={<Wallet size={22} className="text-blue-400" />} label={`Salary Due (${month})`} value={formatCurrency(summary.data.totalDue)} accent="border-blue-500" />
          <StatCard icon={<Wallet size={22} className="text-emerald-400" />} label="Paid" value={formatCurrency(summary.data.totalPaid)} accent="border-emerald-500" />
          <StatCard icon={<Wallet size={22} className="text-red-400" />} label="Remaining" value={formatCurrency(summary.data.totalRemaining)} accent="border-red-500" />
        </div>
      )}

      <div className="card flex p-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            placeholder="Search by name, code or department…"
            className="input pl-10"
          />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : data && data.items.length === 0 ? (
          <EmptyState icon={<UserCheck size={32} />} title="No staff found" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Employee</th>
                    <th className="px-4 py-3">Department</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3 text-right">Monthly Salary</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className={isFetching ? "opacity-60 transition-opacity" : ""}>
                  {data?.items.map((s) => (
                    <tr key={s.id} className="border-b border-slate-800 last:border-0 hover:bg-slate-800/40">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-100">{s.fullName}</div>
                        <div className="text-xs text-slate-500">{s.employeeCode} · {s.designation ?? "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{s.department ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-400">{formatDate(s.joiningDate)}</td>
                      <td className="px-4 py-3 text-right text-slate-200">{formatCurrency(s.monthlySalary)}</td>
                      <td className="px-4 py-3"><ActiveBadge active={s.isActive} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setPayFor(s)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-emerald-400" title="Pay salary">
                            <Wallet size={16} />
                          </button>
                          <button onClick={() => setHistoryFor(s)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-blue-400" title="Payment history">
                            <History size={16} />
                          </button>
                          <button onClick={() => setEditing(s)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-slate-100" title="Edit">
                            <Pencil size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data?.meta} onPageChange={setPage} />
          </>
        )}
      </div>

      {creating && <CreateStaffModal onClose={() => setCreating(false)} />}
      {editing && <EditStaffModal staff={editing} onClose={() => setEditing(null)} />}
      {payFor && <PaySalaryModal staff={payFor} defaultMonth={month} onClose={() => setPayFor(null)} />}
      {historyFor && <SalaryHistoryModal staff={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
};

// ───────────────────────── Create staff ─────────────────────────

const createSchema = z
  .object({
    fullName: z.string().min(2, "Name is required"),
    employeeCode: z.string().optional(),
    designation: z.string().optional(),
    department: z.string().optional(),
    phone: z.string().optional(),
    joiningDate: z.string().min(1, "Joining date is required"),
    monthlySalary: z.coerce.number().nonnegative(),
    createLogin: z.boolean().optional(),
    email: z.string().email("Valid email required").optional().or(z.literal("")),
    password: z.string().optional(),
    role: z.string().optional(),
  })
  .refine((v) => !v.createLogin || (v.email && v.password && v.role), {
    message: "Email, password and role are required for a login",
    path: ["email"],
  });

const CreateStaffModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const create = useCreateStaff();
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    resolver: zodResolver(createSchema),
    defaultValues: { joiningDate: new Date().toISOString().slice(0, 10), monthlySalary: 0 },
  });
  const createLogin = watch("createLogin");

  const onSubmit = handleSubmit(async (values) => {
    try {
      await create.mutateAsync({
        fullName: values.fullName,
        employeeCode: values.employeeCode || undefined,
        designation: values.designation || undefined,
        department: values.department || undefined,
        phone: values.phone || undefined,
        joiningDate: new Date(values.joiningDate).toISOString(),
        monthlySalary: values.monthlySalary,
        ...(values.createLogin
          ? {
              email: values.email || undefined,
              password: values.password || undefined,
              role: values.role as CreateStaffInput["role"],
            }
          : {}),
      });
      toast.success("Staff member created");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Add Staff Member"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} loading={create.isPending}>Create</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField label="Full Name" required error={errors.fullName?.message} {...register("fullName")} />
        <TextField label="Employee Code" placeholder="Auto if blank" {...register("employeeCode")} />
        <TextField label="Designation" {...register("designation")} />
        <TextField label="Department" {...register("department")} />
        <TextField label="Phone" {...register("phone")} />
        <TextField label="Joining Date" type="date" required error={errors.joiningDate?.message} {...register("joiningDate")} />
        <TextField label="Monthly Salary" type="number" step="0.01" error={errors.monthlySalary?.message} {...register("monthlySalary")} />

        <label className="flex items-center gap-2 self-end pb-2 text-sm text-slate-300 sm:col-span-2">
          <input type="checkbox" className="h-4 w-4 rounded border-slate-600 bg-slate-700" {...register("createLogin")} />
          Create a portal login for this employee
        </label>

        {createLogin && (
          <>
            <TextField label="Login Email" type="email" error={errors.email?.message} {...register("email")} />
            <TextField label="Password" type="password" {...register("password")} />
            <SelectField label="Role" className="sm:col-span-2" {...register("role")}>
              <option value="">Select role…</option>
              {STAFF_LOGIN_ROLES.map((r) => (
                <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
              ))}
            </SelectField>
          </>
        )}
      </form>
    </Modal>
  );
};

// ───────────────────────── Edit staff ─────────────────────────

const EditStaffModal: React.FC<{ staff: StaffProfile; onClose: () => void }> = ({ staff, onClose }) => {
  const update = useUpdateStaff();
  const { register, handleSubmit } = useForm({
    defaultValues: {
      fullName: staff.fullName,
      designation: staff.designation ?? "",
      department: staff.department ?? "",
      phone: staff.phone ?? "",
      joiningDate: staff.joiningDate.slice(0, 10),
      monthlySalary: toNumber(staff.monthlySalary),
      isActive: staff.isActive,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync({
        id: staff.id,
        input: {
          fullName: values.fullName,
          designation: values.designation || undefined,
          department: values.department || undefined,
          phone: values.phone || undefined,
          joiningDate: new Date(values.joiningDate).toISOString(),
          monthlySalary: Number(values.monthlySalary),
          isActive: values.isActive,
        },
      });
      toast.success("Staff member updated");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Edit Staff Member"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} loading={update.isPending}>Save changes</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField label="Full Name" {...register("fullName")} />
        <TextField label="Designation" {...register("designation")} />
        <TextField label="Department" {...register("department")} />
        <TextField label="Phone" {...register("phone")} />
        <TextField label="Joining Date" type="date" {...register("joiningDate")} />
        <TextField label="Monthly Salary" type="number" step="0.01" {...register("monthlySalary")} />
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-slate-300">
          <input type="checkbox" className="h-4 w-4 rounded border-slate-600 bg-slate-700" {...register("isActive")} />
          Active
        </label>
      </form>
    </Modal>
  );
};

// ───────────────────────── Pay salary ─────────────────────────

const PaySalaryModal: React.FC<{ staff: StaffProfile; defaultMonth: string; onClose: () => void }> = ({ staff, defaultMonth, onClose }) => {
  const record = useRecordSalary();
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      type: "SALARY" as SalaryTxnType,
      periodMonth: defaultMonth,
      amountPaid: toNumber(staff.monthlySalary),
      paidAt: new Date().toISOString().slice(0, 10),
      note: "",
    },
  });
  const type = watch("type");
  const isSalary = type === "SALARY";

  const onSubmit = handleSubmit(async (values) => {
    if (values.type === "SALARY" && !values.periodMonth) {
      toast.error("Select the month this salary is for");
      return;
    }
    try {
      await record.mutateAsync({
        staffId: staff.id,
        type: values.type,
        periodMonth: values.periodMonth || undefined,
        amountPaid: Number(values.amountPaid),
        paidAt: values.paidAt ? new Date(values.paidAt).toISOString() : undefined,
        note: values.note || undefined,
      });
      toast.success(`${humanizeEnum(values.type)} recorded`);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Record Payment — ${staff.fullName}`}
      description={`Monthly salary ${formatCurrency(staff.monthlySalary)}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="success" onClick={onSubmit} loading={record.isPending}>Record payment</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <SelectField label="Payment type" {...register("type")}>
          {TXN_TYPES.map((t) => (
            <option key={t} value={t}>{humanizeEnum(t)}</option>
          ))}
        </SelectField>
        <TextField
          label={isSalary ? "Period (month)" : "Period (month, optional)"}
          type="month"
          {...register("periodMonth")}
        />
        <TextField label="Amount" type="number" step="0.01" error={errors.amountPaid?.message as string} {...register("amountPaid")} />
        <TextField label="Paid on" type="date" {...register("paidAt")} />
        <TextField label="Note" placeholder={isSalary ? "" : "e.g. reason for advance / overtime hours"} {...register("note")} />
      </form>
    </Modal>
  );
};

// ───────────────────────── Payment history ─────────────────────────

const SalaryHistoryModal: React.FC<{ staff: StaffProfile; onClose: () => void }> = ({ staff, onClose }) => {
  const [page, setPage] = useState(1);
  const summary = useStaffLedgerSummary(staff.id);
  const { data, isLoading, isError, error, refetch, isFetching } = useSalaryTransactions({
    staffId: staff.id,
    page,
    limit: 12,
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Payment History — ${staff.fullName}`}
      description={`${staff.employeeCode} · joined ${formatDate(staff.joiningDate)}`}
      size="xl"
      footer={<Button variant="ghost" onClick={onClose}>Close</Button>}
    >
      <div className="space-y-4">
        {summary.data && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MiniStat label="Net paid (all time)" value={formatCurrency(summary.data.netPaid)} />
            <MiniStat label="Salary" value={formatCurrency(summary.data.byType.SALARY ?? 0)} />
            <MiniStat label="Advances" value={formatCurrency(summary.data.byType.ADVANCE ?? 0)} />
            <MiniStat label="Overtime" value={formatCurrency(summary.data.byType.OVERTIME ?? 0)} />
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-700">
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState error={error} onRetry={refetch} />
          ) : data && data.items.length === 0 ? (
            <EmptyState icon={<History size={28} />} title="No payments recorded yet" description="Record a salary, advance or overtime to start the history." />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-900/40 text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Period</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2">Note</th>
                    </tr>
                  </thead>
                  <tbody className={isFetching ? "opacity-60 transition-opacity" : ""}>
                    {data?.items.map((t) => {
                      const isDeduction = t.type === "DEDUCTION";
                      return (
                        <tr key={t.id} className="border-b border-slate-800 last:border-0">
                          <td className="px-3 py-2 text-slate-300">{formatDateTime(t.paidAt)}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${TXN_BADGE[t.type]}`}>
                              {humanizeEnum(t.type)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-400">{t.periodMonth ?? "—"}</td>
                          <td className={`px-3 py-2 text-right font-medium ${isDeduction ? "text-red-400" : "text-slate-100"}`}>
                            {isDeduction ? "−" : ""}{formatCurrency(t.amount)}
                          </td>
                          <td className="px-3 py-2 text-slate-400">{t.note ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination meta={data?.meta} onPageChange={setPage} />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

const MiniStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3">
    <div className="text-xs text-slate-400">{label}</div>
    <div className="mt-0.5 font-semibold text-slate-100">{value}</div>
  </div>
);
